
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface DiscountDetailedViewProps {
  data: any;
  onClose: () => void;
}

const DiscountDetailedView = ({ data, onClose }: DiscountDetailedViewProps) => {
  if (!data || !data.reporting) {
    return null;
  }

  const { reporting } = data;
  const { beforeDiscount, afterVendorDiscount, afterNestingaleDiscount, discountAnalysis } = reporting;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  const getProfitLossBadge = (status: string) => {
    return status === 'profit' ? (
      <Badge variant="default" className="bg-green-500 text-white">Profit</Badge>
    ) : (
      <Badge variant="destructive">Loss</Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Detailed Analysis View</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700"
        >
          ← Back to History
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Before Discount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Total SKUs:</span>
                <span className="font-medium">{beforeDiscount?.noOfSkus || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Margin:</span>
                <span className="font-medium">{beforeDiscount?.avgMargin || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">After Vendor Discount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SKUs in Profit:</span>
                <span className="font-medium text-green-600">{afterVendorDiscount?.noOfProductsInProfit || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SKUs in Loss:</span>
                <span className="font-medium text-red-600">{afterVendorDiscount?.noOfProductsInLoss || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Margin:</span>
                <span className="font-medium">{afterVendorDiscount?.avgMargin || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">After Nestingale Discount</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SKUs in Profit:</span>
                <span className="font-medium text-green-600">{afterNestingaleDiscount?.noOfProductsInProfit || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SKUs in Loss:</span>
                <span className="font-medium text-red-600">{afterNestingaleDiscount?.noOfProductsInLoss || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Average Margin:</span>
                <span className="font-medium">{afterNestingaleDiscount?.avgMargin || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Discount Analysis Table */}
      <Card>
        <CardHeader>
          <CardTitle>Discount Analysis Table</CardTitle>
          <CardDescription>SKU details with pricing and profit/loss analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product SKU ID</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Wholesale Price</TableHead>
                  <TableHead className="text-right">MAP Price</TableHead>
                  <TableHead className="text-right">Shipping Price</TableHead>
                  <TableHead className="text-right">COGS</TableHead>
                  <TableHead className="text-center">Profit/Loss</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {discountAnalysis && discountAnalysis.length > 0 ? (
                  discountAnalysis.map((item: any, index: number) => (
                    <TableRow key={item.sKU || index}>
                      <TableCell className="font-medium">{item.sKU}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.wholeSalePrice || 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.mapPrice || 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.shippingPrice || 0)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.COGS || 0)}</TableCell>
                      <TableCell className="text-center">
                        {getProfitLossBadge(item.profitOrLoss)}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(item.proposedNestingaleDisc || 0)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                      No discount analysis data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DiscountDetailedView;
